import type { ComponentType } from 'react';
import {
  replaceContextMentionRenderers,
  type ContextMentionRendererProps,
} from '@reflex/ui/client/context-mention-renderers';
import { getPluginComponent, type PluginManifest } from './plugin-registry';

let lastManifests: PluginManifest[] | null = null;

/**
 * Resolve every enabled plugin's `mentionBadgeComponentPath` declarations
 * into the shared `@reflex/ui` registry the composer's {@link MentionNode}
 * consults when decorating a context chip, so a plugin can style its
 * mention chips with live state (e.g. coloring a PR chip by its status).
 *
 * Same shape as `resolveContextUrlMatchers` — both ride on
 * `agentContextProviders` and reconcile on manifest change.
 */
export function resolveContextMentionRenderers(manifests: PluginManifest[]): void {
  if (manifests === lastManifests) return;
  lastManifests = manifests;
  const entries: Array<{ kind: string; Component: ComponentType<ContextMentionRendererProps> }> =
    [];
  const seen = new Set<string>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const provider of manifest.web?.agentContextProviders ?? []) {
      if (!provider.mentionBadgeComponentPath) continue;
      if (seen.has(provider.kind)) continue;
      const Component = getPluginComponent(provider.mentionBadgeComponentPath) as
        | ComponentType<ContextMentionRendererProps>
        | undefined;
      if (!Component) continue;
      seen.add(provider.kind);
      entries.push({ kind: provider.kind, Component });
    }
  }
  replaceContextMentionRenderers(entries);
}
